/**
 * Letter Zoom Animation Styles
 *
 * StyleSheet definitions for the Letter Zoom animation component.
 * Extracted for <300 line file compliance and reusability.
 */

import { StyleSheet, Platform, TextStyle, ViewStyle } from 'react-native';
import { CHROMATIC_COLORS, Y2K_GRADIENT_COLORS } from '../types';
import { createBoxShadow, createTextShadow } from '../../../lib/design-tokens';

// Platform-aware box shadow styles
const badgeShadow = createBoxShadow({
  color: '#000',
  offsetX: 0,
  offsetY: 8,
  blurRadius: 16,
  opacity: 0.3,
  elevation: 16,
});

const continueButtonShadow = createBoxShadow({
  color: '#D4A5FF',
  offsetX: 0,
  offsetY: 4,
  blurRadius: 12,
  opacity: 0.4,
  elevation: 8,
});

// Platform-aware text shadow styles
const scoreTextShadow = createTextShadow({
  color: 'rgba(0, 0, 0, 0.3)',
  offsetX: 0,
  offsetY: 2,
  blurRadius: 4,
});

const continueButtonTextShadow = createTextShadow({
  color: 'rgba(212, 165, 255, 0.6)',
  offsetX: 0,
  offsetY: 0,
  blurRadius: 8,
});

const narratorTextShadow = createTextShadow({
  color: 'rgba(0, 0, 0, 0.8)',
  offsetX: 0,
  offsetY: 1,
  blurRadius: 3,
});

const suspenseTextShadow = createTextShadow({
  color: 'rgba(212, 165, 255, 0.4)',
  offsetX: 0,
  offsetY: 0,
  blurRadius: 8,
});

const chromaticRShadow = createTextShadow({
  color: CHROMATIC_COLORS.r,
  offsetX: 0,
  offsetY: 0,
  blurRadius: 3,
});

const chromaticBShadow = createTextShadow({
  color: CHROMATIC_COLORS.b,
  offsetX: 0,
  offsetY: 0,
  blurRadius: 3,
});

export const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    minHeight: 400,
    ...(Platform.OS === 'web' ? { height: '100dvh' as any } : {}), // Dynamic viewport height for mobile web
  },
  gradient: {
    flex: 1,
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    ...(Platform.select({
      web: {
        // Mobile web (< 768px): 200px for Chrome UI + tab groups
        // Desktop web (>= 768px): 100px (original spacing)
        // Formula: 100px + (100 * (100vw - 768px) / -393)
        // At 768px: 100px, At 375px: 200px
        paddingBottom: 'min(200px, max(100px, calc(100px + (100 * (100vw - 768px) / -393))))',
      },
      default: {
        paddingBottom: 200, // Native mobile: increased spacing
      },
    }) as ViewStyle),
  },
  // Letter Zoom styles
  letterZoomContainer: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    overflow: 'visible',
  },
  letterZoomText: {
    fontSize: 18,
    fontWeight: '700',
    color: '#FFFFFF',
    textAlign: 'center',
    lineHeight: 38,
  } as TextStyle,
  letterZoomChromaticR: {
    position: 'absolute',
    color: 'transparent',
    opacity: 0.4,
    ...chromaticRShadow,
  } as TextStyle,
  letterZoomChromaticB: {
    position: 'absolute',
    color: 'transparent',
    opacity: 0.4,
    ...chromaticBShadow,
  } as TextStyle,
  // Reading phase styles
  glassOverlayTop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 100,
    zIndex: 10,
  },
  glassOverlayBottom: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 100,
    zIndex: 10,
  },
  focusGlowZone: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 60,
    top: '50%',
    transform: [{ translateY: -30 }],
    zIndex: 5,
  },
  readingContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '50%',
    width: '100%',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  readingLine: {
    fontSize: 16,
    fontWeight: '500',
    color: '#d0d0d8',
    textAlign: 'center',
    lineHeight: 38,
    height: 38,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  readingLineFocused: {
    fontSize: 17,
    fontWeight: '600',
    color: '#FFFFFF',
    transform: [{ scale: 1.02 }],
  },
  // Badge styles
  badgeContainer: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    // Match animation background to prevent white flash during reveal
    backgroundColor: 'transparent',
  },
  badge: {
    paddingHorizontal: 32,
    paddingVertical: 20,
    borderRadius: 16,
    alignItems: 'center',
    overflow: 'hidden',
    ...badgeShadow,
  } as ViewStyle,
  scoreText: {
    fontSize: 48,
    fontWeight: '800',
    color: '#FFFFFF',
    ...scoreTextShadow,
  } as TextStyle,
  categoryLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.9)',
    marginTop: 4,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  // UI elements
  skipHint: {
    position: 'absolute',
    ...(Platform.select({
      web: {
        // Mobile web (< 768px): 220px for Chrome UI + tab groups
        // Desktop web (>= 768px): 120px (original spacing)
        // Formula: 120px + (100 * (100vw - 768px) / -393)
        // At 768px: 120px, At 375px: 220px
        bottom: 'min(220px, max(120px, calc(120px + (100 * (100vw - 768px) / -393))))',
      },
      default: {
        bottom: 220, // Native mobile: increased spacing
      },
    }) as ViewStyle),
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.5)',
    textAlign: 'center',
  },
  // Continue button wrapper with Y2K animated border (AI pointer)
  // Multi-agent consensus: Gemini 9/10, GPT-5 8/10, @code-reviewer, @cto APPROVED
  // Rotating gradient guides user attention to CTA after badge reveal
  continueButtonWrapper: {
    position: 'absolute',
    ...(Platform.select({
      web: {
        // Mobile web (< 768px): 140px base (Chrome UI + Tab Groups + Touch buffer)
        // Desktop web (>= 768px): 60px (original spacing)
        // Formula: 60px + (80 * (100vw - 768px) / -393)
        // At 768px: 60px, At 375px: 140px
        // Plus env(safe-area-inset-bottom) for iOS Safari notch
        bottom: 'max(min(140px, max(60px, calc(60px + (80 * (100vw - 768px) / -393)))), calc(min(140px, max(60px, calc(60px + (80 * (100vw - 768px) / -393)))) + env(safe-area-inset-bottom)))',
      },
      default: {
        bottom: 140, // Match mobile web positioning for native
      },
    }) as ViewStyle),
    alignItems: 'center',
    justifyContent: 'center',
    // Padding for the 2px border to show
    padding: 2,
    borderRadius: 26, // continueButton radius (24) + padding (2)
    overflow: 'hidden',
  },
  // Rotating gradient container (GPU-accelerated transform:rotate) - Web conic-gradient
  rotatingBorderContainer: {
    position: 'absolute',
    top: -50, // Overflow to ensure full coverage during rotation
    left: -50,
    right: -50,
    bottom: -50,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // SVG spark container for native (traveling spark effect via strokeDasharray)
  // Positioned to overlay button exactly, rotates to move the spark around border
  sparkBorderContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  // The gradient that rotates (larger than container to avoid gaps)
  rotatingBorderGradient: {
    width: 400,
    height: 400,
    borderRadius: 200,
  },
  // Static border for prefers-reduced-motion users (WCAG 2.1 compliance)
  staticBorderGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 26,
  },
  continueButton: {
    borderRadius: 24,
    overflow: 'hidden',
    // Solid background to mask the rotating gradient, revealing only border
    backgroundColor: '#0a0012',
    ...continueButtonShadow,
  } as ViewStyle,
  continueButtonGradient: {
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  continueButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    letterSpacing: 0.5,
    ...continueButtonTextShadow,
  } as TextStyle,
  // DATA-BASED REVEAL: Waiting state styles (shown when animation reaches revelation but API not ready)
  waitingStateContainer: {
    opacity: 0.8,
  },
  waitingText: {
    marginLeft: 8,
    opacity: 0.9,
  },
  // Progress bar
  topLoaderContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    zIndex: 10,
  },
  topLoaderBar: {
    height: '100%',
    borderRadius: 0,
  },
  // Glitch progress
  glitchProgressContainer: {
    position: 'absolute',
    top: 12,
    right: 0,
    alignItems: 'flex-end',
    zIndex: 100,
    paddingRight: 24,
  },
  glitchProgressText: {
    fontFamily: Platform.OS === 'web' ? 'Courier New, monospace' : 'Courier',
    fontSize: 12,
    fontWeight: '700',
    color: Y2K_GRADIENT_COLORS.lavender,
    letterSpacing: 2,
    textTransform: 'uppercase',
  },
  glitchLayerR: {
    position: 'absolute',
    color: Y2K_GRADIENT_COLORS.pink,
    opacity: 0.5,
    transform: [{ translateX: -1 }, { translateY: 0.5 }],
  },
  glitchLayerB: {
    position: 'absolute',
    color: Y2K_GRADIENT_COLORS.mint,
    opacity: 0.5,
    transform: [{ translateX: 1 }, { translateY: -0.5 }],
  },
  // Narrator subtitle styles (reality TV commentary overlay)
  narratorSubtitleContainer: {
    position: 'absolute',
    ...(Platform.select({
      web: {
        // Mobile web (< 768px): 260px for Chrome UI + tab groups
        // Desktop web (>= 768px): 100px (original spacing)
        // Formula: 100px + (160 * (100vw - 768px) / -393)
        // At 768px: 100px, At 375px: 260px
        bottom: 'min(260px, max(100px, calc(100px + (160 * (100vw - 768px) / -393))))',
      },
      default: {
        bottom: 260, // Native mobile: increased spacing
      },
    }) as ViewStyle),
    left: 32,
    right: 32,
    zIndex: 50,
    alignItems: 'center',
  },
  narratorSubtitle: {
    backgroundColor: 'rgba(10, 0, 18, 0.85)',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    maxWidth: 400,
  },
  narratorSubtitleText: {
    // Industry-standard sans-serif for subtitle readability (Netflix/BBC pattern)
    // Inter (brand font) with robust system fallback for cross-platform consistency
    fontFamily: Platform.OS === 'web'
      ? 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      : 'System',
    fontSize: 15,
    fontWeight: '500',
    color: '#FFFFFF',
    textAlign: 'center',
    letterSpacing: 0.2,
    // Text shadow for contrast against animated background (accessibility)
    ...narratorTextShadow,
  } as TextStyle,
  // ============================================================================
  // SUSPENSE PHASE: Theatrical mode waiting state (breathing + message rotation)
  // ============================================================================
  suspenseContainer: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
    zIndex: 20,
  },
  suspenseSpinner: {
    marginBottom: 8,
  },
  suspenseMessage: {
    fontFamily: Platform.OS === 'web'
      ? 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      : 'System',
    fontSize: 16,
    fontWeight: '500',
    color: Y2K_GRADIENT_COLORS.lavender,
    textAlign: 'center',
    letterSpacing: 0.3,
    opacity: 0.9,
    maxWidth: 280,
    ...suspenseTextShadow,
  } as TextStyle,
});
